import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Car, MapPin, Calendar, Users, Shield, Info, CheckCircle, AlertTriangle, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SelfDriveTermsModal from '../components/SelfDriveTermsModal';

interface Vehicle {
  id: string;
  name: string;
  image: string;
  type: string;
  capacity: string;
  price: number;
  features: string[];
  recommended: boolean;
}

interface Extra {
  id: string;
  name: string;
  price: number;
  description: string;
}

const vehicles: Vehicle[] = [
  {
    id: 'land-cruiser',
    name: 'Toyota Land Cruiser',
    image: 'https://imgur.com/GemIpY3.jpg',
    type: '4x4 SUV',
    capacity: '4-7 passengers',
    price: 150,
    features: [
      'High ground clearance',
      'Excellent off-road capability',
      'Air conditioning',
      'Roof rack',
      'Comfortable seating'
    ],
    recommended: true
  },
  {
    id: 'lexus-rx',
    name: 'Lexus RX - Luxury & Comfort',
    image: 'https://imgur.com/lY6h8Dz.jpg',
    type: 'Luxury SUV',
    capacity: '4-5 passengers',
    price: 120,
    features: [
      'Smooth, comfortable ride',
      'Spacious interior',
      'Premium sound system',
      'Advanced safety features',
      'Ample luggage space'
    ],
    recommended: false
  },
  {
    id: 'hiace-safari',
    name: 'Toyota HiAce Safari Van - Perfect for Group Safaris',
    image: 'https://imgur.com/yDANaU5.jpg',
    type: 'Safari Van',
    capacity: '7-9 passengers',
    price: 120,
    features: [
      'Pop-up roof for wildlife viewing',
      '4x4 capability for off-road',
      'Spacious seating arrangement',
      'Air conditioning',
      'Reinforced bull bar'
    ],
    recommended: false
  },
  {
    id: 'land-cruiser-70',
    name: 'Toyota Land Cruiser 70 Series - Ultimate Safari Vehicle',
    image: 'https://imgur.com/YABl8r4.jpg',
    type: '4x4 SUV',
    capacity: 'Up to 7 passengers',
    price: 155,
    features: [
      'Powerful 4x4 capability',
      'Rugged durability',
      'Roof rack for extra storage',
      'Perfect for game drives',
      'Excellent for rough terrain'
    ],
    recommended: true
  }
];

const extras: Extra[] = [
  {
    id: 'roof-tent',
    name: 'Roof-top Tent',
    price: 30,
    description: 'Comfortable camping solution mounted on vehicle roof'
  },
  {
    id: 'gps',
    name: 'GPS Navigation',
    price: 10,
    description: 'Pre-loaded with Uganda maps and points of interest'
  },
  {
    id: 'camping-gear',
    name: 'Camping Equipment',
    price: 40,
    description: 'Complete set including tent, sleeping bags, and cooking gear'
  },
  {
    id: 'fuel-can',
    name: 'Extra Fuel Can',
    price: 15,
    description: '20L jerry can for extended trips'
  },
  {
    id: 'fridge',
    name: 'Portable Fridge',
    price: 25,
    description: '12V cooler for food and drinks'
  },
  {
    id: 'binoculars',
    name: 'Binoculars',
    price: 10,
    description: 'High-quality optics for wildlife viewing'
  },
  {
    id: 'wifi',
    name: 'Mobile Wi-Fi',
    price: 20,
    description: 'Portable internet device with daily data allowance'
  }
];

const safariPackages = [
  {
    id: 'finest',
    title: "12-Day Uganda's Finest",
    image: 'https://imgur.com/YmQ8sKD.jpg',
    price: 2400,
    duration: '12 days',
    description: 'Experience the ultimate Ugandan adventure with this comprehensive safari package.',
    route: '/self-drive-safaris/ugandas-finest'
  },
  {
    id: 'primate',
    title: '8-Day The Primate Express',
    image: 'https://imgur.com/PK2H77z.jpg',
    price: 2100,
    duration: '8 days',
    description: 'Focus on Uganda\'s incredible primates including gorillas and chimpanzees.',
    route: '/self-drive-safaris/primate-express'
  },
  {
    id: 'circuit',
    title: '24-Day The Grand Circuit',
    image: 'https://imgur.com/2xNtdHj.jpg',
    price: 5100,
    duration: '24 days',
    description: 'A comprehensive exploration of Uganda\'s most iconic destinations.',
    route: '/self-drive-safaris/grand-circuit'
  }
];

const SelfDriveSafaris = () => {
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [duration, setDuration] = useState<number>(1);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const { t } = useTranslation();

  const calculateTotal = () => {
    const vehicle = vehicles.find(v => v.id === selectedVehicle);
    const vehicleCost = vehicle ? vehicle.price * duration : 0;
    const extrasCost = selectedExtras.reduce((total, extraId) => {
      const extra = extras.find(e => e.id === extraId);
      return total + (extra ? extra.price * duration : 0);
    }, 0);
    return vehicleCost + extrasCost;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const selectedVehicleData = vehicles.find(v => v.id === selectedVehicle);
      const selectedExtrasData = selectedExtras.map(id => 
        extras.find(e => e.id === id)?.name
      ).filter(Boolean);

      const formBody = {
        'form-name': 'self-drive-booking',
        ...formData,
        vehicle: selectedVehicleData?.name || '',
        extras: selectedExtrasData.join(', '),
        startDate,
        duration: duration.toString(),
        total: calculateTotal().toString()
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your booking. Please try again.');
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/TvdP6Mt.jpg"
            alt="Self-drive safari"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/uganda-trips" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">{t('pages.selfDriveSafaris.title')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('pages.selfDriveSafaris.subtitle')}
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">4+</div>
                <div className="text-sm text-white/80">{t('common.vehicleTypes')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">24/7</div>
                <div className="text-sm text-white/80">{t('common.support')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">100%</div>
                <div className="text-sm text-white/80">{t('common.insured')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-ta-green">4.9/5</div>
                <div className="text-sm text-white/80">{t('common.rating')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safari Packages Section */}
      <section className="py-8 sm:py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">{t('adventures.safariPackages')}</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            {safariPackages.map((pkg) => (
              <div 
                key={pkg.id}
                className="group bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative h-32 sm:h-48">
                  <img 
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all duration-300"></div>
                  <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 text-white">
                    <h3 className="text-sm sm:text-lg font-semibold line-clamp-1">{pkg.title}</h3>
                    <p className="text-xs sm:text-sm text-white/90">{pkg.duration}</p>
                  </div>
                </div>
                
                <div className="p-2 sm:p-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4 line-clamp-2 sm:line-clamp-3">{pkg.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500">{t('common.from')}</p>
                      <p className="text-sm sm:text-xl font-bold text-ta-green">${pkg.price}</p>
                    </div>
                    <Link
                      to={pkg.route}
                      className="px-2 sm:px-4 py-1 sm:py-2 bg-ta-green text-white text-xs sm:text-sm rounded-lg hover:bg-ta-green-dark transition-all duration-300 transform hover:scale-105"
                    >
                      {t('experiences.viewDetails')}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Selection */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">{t('pages.selfDriveSafaris.chooseVehicle')}</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {vehicles.map((vehicle) => (
              <div 
                key={vehicle.id}
                onClick={() => setSelectedVehicle(vehicle.id)}
                className={`group bg-white rounded-lg border ${
                  selectedVehicle === vehicle.id 
                    ? 'border-ta-green ring-2 ring-ta-green/20' 
                    : 'border-gray-200'
                } overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer`}
              >
                <div className="relative h-32 sm:h-40">
                  <img 
                    src={vehicle.image}
                    alt={vehicle.name}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  {vehicle.recommended && (
                    <div className="absolute top-2 left-2 bg-ta-green text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      {t('common.recommended')}
                    </div>
                  )}
                </div>
                
                <div className="p-2 sm:p-3">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 line-clamp-1">{vehicle.name}</h3>
                  <div className="flex items-center text-[10px] sm:text-xs text-gray-600 mb-1">
                    <Car className="h-3 w-3 text-ta-green mr-1" />
                    {vehicle.type}
                  </div>
                  
                  <div className="space-y-1 mb-2">
                    <div className="flex items-center text-[10px] sm:text-xs text-gray-600">
                      <Users className="h-3 w-3 text-ta-green mr-1" />
                      {vehicle.capacity}
                    </div>
                    <div className="flex items-center text-[10px] sm:text-xs text-gray-600">
                      <DollarSign className="h-3 w-3 text-ta-green mr-1" />
                      ${vehicle.price}/{t('common.day')}
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    {vehicle.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center text-[10px] sm:text-xs text-gray-600">
                        <CheckCircle className="h-2.5 w-2.5 text-ta-green mr-1 flex-shrink-0" />
                        <span className="truncate">{feature}</span>
                      </div>
                    ))}
                    {vehicle.features.length > 3 && (
                      <div className="text-[10px] sm:text-xs text-ta-green">
                        +{vehicle.features.length - 3} {t('common.moreFeatures')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extras Selection */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('pages.selfDriveSafaris.customizeExperience')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {extras.map((extra) => (
              <div 
                key={extra.id}
                className={`bg-white rounded-lg border ${
                  selectedExtras.includes(extra.id)
                    ? 'border-ta-green ring-2 ring-ta-green/20'
                    : 'border-gray-200'
                } p-4 cursor-pointer hover:shadow-md transition-all duration-300`}
                onClick={() => {
                  if (selectedExtras.includes(extra.id)) {
                    setSelectedExtras(selectedExtras.filter(id => id !== extra.id));
                  } else {
                    setSelectedExtras([...selectedExtras, extra.id]);
                  }
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{extra.name}</h3>
                  <div className="text-ta-green font-medium">${extra.price}/{t('common.day')}</div>
                </div>
                <p className="text-sm text-gray-600">{extra.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('common.importantInfo')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <Shield className="h-5 w-5 text-ta-green mr-2" />
                {t('pages.selfDriveSafaris.requirements')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.validDriversLicense')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.minimumAge')}: 23 {t('common.years')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">2+ {t('common.years')} {t('common.drivingExperience')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.creditCardForDeposit')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <Info className="h-5 w-5 text-ta-green mr-2" />
                {t('pages.selfDriveSafaris.roadConditions')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.mainHighwaysPaved')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.parkRoadsUnpaved')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.fourWDRecommended')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.seasonalVariations')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                <AlertTriangle className="h-5 w-5 text-ta-green mr-2" />
                {t('pages.selfDriveSafaris.safetyGuidelines')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.driveOnLeft')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.avoidNightDriving')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.carryEmergencyContacts')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-ta-green mr-2 mt-1" />
                  <span className="text-gray-600">{t('common.followParkRegulations')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{t('common.bookYourSelfDriveSafari')}</h2>
          
          <form 
            name="self-drive-booking"
            method="POST"
            data-netlify="true"
            onSubmit={handleSubmit}
            className="bg-white rounded-lg shadow-sm p-6 space-y-6"
          >
            <input type="hidden" name="form-name" value="self-drive-booking" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.booking.firstName')} *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder={t('common.enterYourName')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.booking.email')} *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder={t('common.enterYourEmail')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.startDate')}</label>
                <input
                  type="date"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.duration')} ({t('common.days')})</label>
                <select
                  name="duration"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? t('common.day') : t('common.days')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('common.summary')}</h3>
              
              <div className="space-y-2 mb-4">
                {selectedVehicle && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('common.vehicleRental')}</span>
                    <span className="font-medium">${vehicles.find(v => v.id === selectedVehicle)?.price ?? 0} × {duration} {t('common.days')}</span>
                  </div>
                )}
                
                {selectedExtras.map(extraId => {
                  const extra = extras.find(e => e.id === extraId);
                  return (
                    <div key={extraId} className="flex justify-between text-sm">
                      <span className="text-gray-600">{extra?.name}</span>
                      <span className="font-medium">${extra?.price ?? 0} × {duration} {t('common.days')}</span>
                    </div>
                  );
                })}
              </div>
              
              <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">{t('common.total')}</span>
                <span className="text-xl font-bold text-ta-green">${calculateTotal()}</span>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
            >
              {t('common.bookNow')}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SelfDriveSafaris;